import { 
  Box, 
  Flex, 
  useDisclosure, 
  VStack, 
  HStack, 
  Text, 
  Heading, 
  SimpleGrid,
  useToast,
  Card,
  CardBody,
} from "@chakra-ui/react";
import React, { useEffect, useMemo, useState } from "react";
import AddButton from "../../components/shared/AddButton";
import ExportEmployeeBtn from "../../components/shared/ExportEmployeeBtn";
import EmployeeTable from "../../components/tables/EmployeeTable";
import Pagination from "../../components/pagination/Pagination";
import { useNavigate } from "react-router-dom";
import {
  useGetEmployees,
  useGetArchivedEmployees,
} from "../../services/employeeService";
import { useGetAdminEmployees } from "../../services/companyService";
import { useUserContext } from "../../context/UserContext";
import { CSVLink } from "react-csv";
import { TbCloudUpload } from "react-icons/tb";
import { AddIcon } from "@chakra-ui/icons";

import { FiUsers, FiUserPlus, FiUserCheck, FiUserX } from "react-icons/fi";
import BulkUpload from "../../components/BulkUpload";
import BranchFilter from "../../components/BranchFilter";




const Employees = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useUserContext();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedBranch, setSelectedBranch] = useState("all");
  const [statusFilter, setStatusFilter] = useState("active"); // "active" | "archived" | "all"

  // Role-based endpoint selection
  const isCompanyAdmin = user?.role === 'company_admin';
  
  // Company admins: use admin endpoints with branch filtering
  const adminEmployeesData = useGetAdminEmployees({ 
    enabled: isCompanyAdmin,
    // Show all employees by default, only filter when specific branch is selected
    ...(selectedBranch !== 'all' && { branch_id: selectedBranch })
  });
  
  // Branch users: use regular endpoints (no branch filtering needed)
  const regularEmployeesData = useGetEmployees({ 
    enabled: !isCompanyAdmin
  });

  // Archived employees endpoint - ALWAYS fetch to get accurate count
  const archivedEmployeesData = useGetArchivedEmployees({
    enabled: true, // Always fetch to show accurate count
    branch_id: selectedBranch !== 'all' ? selectedBranch : null,
    page: 1,
    limit: 100
  });
  
  // Select appropriate data based on user role and filter
  const getEmployeesIsLoading = statusFilter === "archived"
    ? archivedEmployeesData.archivedEmployeesIsLoading
    : isCompanyAdmin 
      ? adminEmployeesData.adminEmployeesIsLoading 
      : regularEmployeesData.getEmployeesIsLoading;
    
  const getEmployeesData = statusFilter === "archived"
    ? archivedEmployeesData.archivedEmployeesData
    : isCompanyAdmin 
      ? adminEmployeesData.adminEmployees 
      : regularEmployeesData.getEmployeesData;
    
  const refetchGetEmployees = statusFilter === "archived"
    ? archivedEmployeesData.refetchArchivedEmployees
    : isCompanyAdmin 
      ? adminEmployeesData.refetchAdminEmployees 
      : regularEmployeesData.refetchGetEmployees;

  const itemsPerPage = 20;



  const handleEmployeeData = useMemo(() => {
    // Archived employees endpoint returns { employees: [...], total: X }
    if (statusFilter === "archived") {
      return getEmployeesData || {};
    }
    // Handle different data structures for admin vs branch users
    if (isCompanyAdmin) {
      // Admin endpoint returns { data: { employees: [...], total: 15 } }
      return getEmployeesData?.data || {};
    } else {
      // Branch user endpoint might return different structure
      // Check if it's already the data object or if it needs extraction
      if (getEmployeesData?.data) {
        return getEmployeesData.data;
      } else if (getEmployeesData?.employees) {
        return getEmployeesData;
      } else {
        return getEmployeesData || {};
      }
    }
  }, [getEmployeesData, isCompanyAdmin, statusFilter]);

  // Calculate employee statistics
  const employeeStats = useMemo(() => {
    // Ensure we have an array of employees
    const employees = Array.isArray(handleEmployeeData?.employees) 
      ? handleEmployeeData.employees 
      : Array.isArray(handleEmployeeData) 
        ? handleEmployeeData 
        : [];
    
    // Get archived count from dedicated archived employees hook
    const archivedEmployees = Array.isArray(archivedEmployeesData?.archivedEmployeesData?.employees)
      ? archivedEmployeesData.archivedEmployeesData.employees.length
      : 0;
        
    const totalEmployees = employees.length;
    const activeEmployees = employees.filter(emp => emp?.status === 'active').length;
    const newEmployees = employees.filter(emp => {
      if (!emp?.hire_date) return false;
      const hireDate = new Date(emp.hire_date);
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      return hireDate > thirtyDaysAgo;
    }).length;

    return {
      total: totalEmployees,
      active: activeEmployees,
      archived: archivedEmployees,
      new: newEmployees
    };
  }, [handleEmployeeData, archivedEmployeesData]);

  // Note: Admin employees endpoint handles pagination automatically
  // No need for manual pagination control

  return (
    <>
      <Box w={"100%"} bg="gray.50" minH="100vh">
        {/* Page Header */}
        <VStack spacing={6} p={6} align="stretch">
          <Heading size="lg" color="gray.900" fontWeight="600">
            Employee Management
          </Heading>
          
          {/* Statistics Cards */}
          <SimpleGrid columns={{ base: 1, sm: 2, md: 2, lg: 4 }} spacing={6}>
            <StatCard
              title="Total Employees"
              value={employeeStats.total}
              icon={<FiUsers />}
              color="blue"
            />
            <StatCard
              title="Active Employees"
              value={employeeStats.active}
              icon={<FiUserCheck />}
              color="green"
              onClick={() => setStatusFilter("active")}
              isActive={statusFilter === "active"}
            />
            <StatCard
              title="Archived Employees"
              value={employeeStats.archived}
              icon={<FiUserX />}
              color="red"
              onClick={() => setStatusFilter("archived")}
              isActive={statusFilter === "archived"}
            />
            <StatCard
              title="New This Month"
              value={employeeStats.new}
              icon={<FiUserPlus />}
              color="purple"
            />
          </SimpleGrid>

          {/* Branch Filter */}
          <Card>
            <CardBody>
              <HStack spacing={4} wrap="wrap" justify="space-between">
                <BranchFilter
                  selectedBranch={selectedBranch}
                  onBranchChange={setSelectedBranch}
                  showLabel={true}
                  maxW="300px"
                />
                
                <Flex gap={3}>
                  <AddButton
                    onClick={() => {
                      sessionStorage.setItem("todo", "new");
                      sessionStorage.removeItem("employee");
                      navigate("add-employee");
                    }}
                    icon={<AddIcon />}
                    name={"Add Employee"}
                  />
                  <AddButton
                    onClick={onOpen}
                    icon={<TbCloudUpload />}
                    name={"Bulk Upload"}
                  />

                  <CSVLink
                    data={
                      Array.isArray(handleEmployeeData?.employees) 
                        ? handleEmployeeData.employees 
                        : Array.isArray(handleEmployeeData) 
                          ? handleEmployeeData 
                          : []
                    }
                    onClick={(e) => e.stopPropagation()}
                  >
                    <ExportEmployeeBtn />
                  </CSVLink>
                </Flex>
              </HStack>
            </CardBody>
          </Card>

          {/* Employee Table */}
          <Box bg="white" borderRadius="16px" shadow="sm" border="1px solid" borderColor="gray.200" overflow="hidden">
            {getEmployeesIsLoading ? (
              <Box p={8} textAlign="center">
                <Text>Loading employees...</Text>
              </Box>
            ) : (
              <EmployeeTable
                employeeData={
                  Array.isArray(handleEmployeeData?.employees) 
                    ? handleEmployeeData.employees 
                    : Array.isArray(handleEmployeeData) 
                      ? handleEmployeeData 
                      : []
                }
                getEmployeesIsLoading={getEmployeesIsLoading}
                refetchGetEmployees={refetchGetEmployees}
                statusFilter={statusFilter}
              />
            )}
            {(Array.isArray(handleEmployeeData?.employees) ? handleEmployeeData.employees.length : 0) > 0 &&
            (handleEmployeeData?.total_count || 0) > 20 ? (
              <Box p={4} borderTop="1px solid" borderColor="gray.200">
                <Pagination
                  totalItems={
                    handleEmployeeData?.total_count || 
                    (Array.isArray(handleEmployeeData?.employees) ? handleEmployeeData.employees.length : 0) || 
                    0
                  }
                  itemsPerPage={itemsPerPage}
                  currentPage={currentPage}
                  onPageChange={setCurrentPage}
                />
              </Box>
            ) : null}
          </Box>
        </VStack>
      </Box>


      <BulkUpload
        isOpen={isOpen}
        onClose={onClose}
        onSuccess={(results) => {
          // Refresh employee data after successful upload
          refetchGetEmployees();
          toast({
            title: "Bulk upload completed!",
            description: `Successfully added ${results?.success_count || 0} employees.`,
            status: "success",
            duration: 5000,
            isClosable: true,
          });
        }}
      />

    </>
  );
};

// StatCard Component
const StatCard = ({ title, value, icon, color, onClick, isActive }) => {
  const colorScheme = {
    blue: { bg: "blue.50", color: "blue.600", border: "blue.200" },
    green: { bg: "green.50", color: "green.600", border: "green.200" },
    red: { bg: "red.50", color: "red.600", border: "red.200" },
    purple: { bg: "purple.50", color: "purple.600", border: "purple.200" },
  };

  const colors = colorScheme[color] || colorScheme.blue;
  const isClickable = onClick !== undefined;

  return (
    <Box
      bg={isActive ? colors.bg : "white"}
      p={6}
      borderRadius="16px"
      shadow="sm"
      border="2px solid"
      borderColor={isActive ? colors.border : "gray.200"}
      _hover={{ shadow: "md", cursor: isClickable ? "pointer" : "default" }}
      transition="all 0.2s"
      onClick={onClick}
    >
      <HStack spacing={4} align="center">
        <Box
          p={3}
          borderRadius="12px"
          bg={colors.bg}
          color={colors.color}
          border="1px solid"
          borderColor={colors.border}
        >
          {icon}
        </Box>
        <VStack spacing={1} align="start" flex={1}>
          <Text fontSize="sm" color="gray.600" fontWeight="500">
            {title}
          </Text>
          <Text fontSize="2xl" color="gray.900" fontWeight="700">
            {value}
          </Text>
        </VStack>
      </HStack>
    </Box>
  );
};

export default Employees;
